import { errorText } from "@/components/forms/form-errors";
import { FocusPanel } from "@/components/focus-panel";
import { Form, Head } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import SettingsLayout from "@/layouts/settings_layout";
import {
  userInvitationsPath,
  userMemberActivationPath,
  userMemberLocationAccessPath,
  userMemberPath,
} from "@/routes";

type Location = { id: string; name: string };
type Member = {
  id: string;
  email_address: string;
  first_name: string;
  last_name: string;
  role: string;
  locale: string;
  deactivated_at?: string | null;
  location_ids: string[];
  historical_location_ids: string[];
  can_deactivate: boolean;
};
type Invitation = {
  id: string;
  email_address: string;
  role: string;
  locale: string;
  active: boolean;
  location_ids: string[];
};

export default function TeamSettings({
  members,
  invitations,
  locations,
  retired_locations: retiredLocations,
  can_invite: canInvite,
  can_manage_roles: canManageRoles,
}: {
  members: Member[];
  invitations: Invitation[];
  locations: Location[];
  retired_locations: Location[];
  can_invite: boolean;
  can_manage_roles: boolean;
}) {
  const { t } = useTranslation();
  return (
    <SettingsLayout>
      <Head title={t("common.team")} />
      <div className="space-y-6">
        <header>
          <h1 className="text-xl font-semibold">{t("common.team")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("settings.team_description")}
          </p>
        </header>
        <FocusPanel
          trigger={t("team.invite_title")}
          errorTargets={{ email_address: "invite_email", role: "invite_role" }}
          title={t("team.invite_title")}
          description={t("team.invite_description")}
        >
          <Card>
            <CardContent>
              <Form action={userInvitationsPath()} method="post" resetOnSuccess>
                {({ errors, processing }) => (
                  <FieldGroup>
                    {!canInvite && (
                      <p
                        role="status"
                        className="text-sm text-muted-foreground"
                      >
                        {t("access.team_limit")}
                      </p>
                    )}
                    <div className="grid gap-4 md:grid-cols-3">
                      <Field>
                        <FieldLabel htmlFor="invite_email">
                          {t("team.email")}
                        </FieldLabel>
                        <Input
                          id="invite_email"
                          name="invitation[email_address]"
                          type="email"
                          required
                        />
                        <FieldError>
                          {errorText(errors.email_address)}
                        </FieldError>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="invite_role">
                          {t("team.role")}
                        </FieldLabel>
                        <NativeSelect
                          id="invite_role"
                          name="invitation[role]"
                          defaultValue="member"
                        >
                          <NativeSelectOption value="admin">
                            {t("team.admin")}
                          </NativeSelectOption>
                          <NativeSelectOption value="manager">
                            {t("team.manager")}
                          </NativeSelectOption>
                          <NativeSelectOption value="member">
                            {t("team.member")}
                          </NativeSelectOption>
                        </NativeSelect>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="invite_locale">
                          {t("common.language")}
                        </FieldLabel>
                        <NativeSelect
                          id="invite_locale"
                          name="invitation[locale]"
                          defaultValue="en"
                        >
                          <NativeSelectOption value="en">
                            English
                          </NativeSelectOption>
                          <NativeSelectOption value="ro">
                            Română
                          </NativeSelectOption>
                        </NativeSelect>
                      </Field>
                    </div>
                    <Field>
                      <FieldLabel>{t("common.locations")}</FieldLabel>
                      <div className="flex flex-wrap gap-4">
                        {locations.map((location) => (
                          <label
                            key={location.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              name="invitation[location_ids][]"
                              value={location.id}
                              defaultChecked
                            />
                            {location.name}
                          </label>
                        ))}
                      </div>
                      <FieldError>{errorText(errors.locations)}</FieldError>
                    </Field>
                    <FieldError>{errorText(errors.base)}</FieldError>
                    <Button
                      type="submit"
                      className="w-fit"
                      disabled={!canInvite || processing}
                    >
                      {processing && <Spinner />}
                      {t("team.send_invitation")}
                    </Button>
                  </FieldGroup>
                )}
              </Form>
            </CardContent>
          </Card>
        </FocusPanel>
        <div className="space-y-4">
          {members.map((member) => (
            <Card key={member.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>
                      {[member.first_name, member.last_name]
                        .filter(Boolean)
                        .join(" ") || member.email_address}
                    </CardTitle>
                    <CardDescription>{member.email_address}</CardDescription>
                  </div>
                  <Badge
                    variant={member.deactivated_at ? "outline" : "secondary"}
                  >
                    {member.deactivated_at
                      ? t("team.inactive")
                      : t(`team.${member.role}`)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <FocusPanel
                  trigger={t("access.edit_member")}
                  title={`${member.first_name || member.email_address} · ${t("access.edit_member")}`}
                  description={t("access.member_impact")}
                >
                  <Form action={userMemberPath(member.id)} method="patch">
                    {({ errors }) => (
                      <>
                        <div className="flex gap-2">
                          <NativeSelect
                            aria-label={`${t("team.role")} · ${member.email_address}`}
                            name="user[role]"
                            defaultValue={member.role}
                            disabled={!canManageRoles}
                          >
                            <NativeSelectOption value="admin">
                              {t("team.admin")}
                            </NativeSelectOption>
                            <NativeSelectOption value="manager">
                              {t("team.manager")}
                            </NativeSelectOption>
                            <NativeSelectOption value="member">
                              {t("team.member")}
                            </NativeSelectOption>
                          </NativeSelect>
                          <Button
                            size="sm"
                            variant="outline"
                            type="submit"
                            disabled={!canManageRoles}
                          >
                            {t("team.save_role")}
                          </Button>
                        </div>
                        <FieldError>
                          {errorText(errors.base) || errorText(errors.role)}
                        </FieldError>
                      </>
                    )}
                  </Form>
                  <Form
                    action={userMemberLocationAccessPath(member.id)}
                    method="patch"
                  >
                    {({ errors }) => (
                      <>
                        <p className="text-muted-foreground mb-2 text-sm">
                          {t("team.active_access")}
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          {locations.map((location) => (
                            <label
                              key={location.id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <Checkbox
                                name="user[location_ids][]"
                                value={location.id}
                                defaultChecked={member.location_ids.includes(
                                  location.id,
                                )}
                                disabled={
                                  !canManageRoles || member.role === "admin"
                                }
                              />
                              {location.name}
                            </label>
                          ))}
                          <Button
                            size="sm"
                            variant="outline"
                            type="submit"
                            disabled={
                              !canManageRoles || member.role === "admin"
                            }
                          >
                            {t("team.save_access")}
                          </Button>
                        </div>
                        {member.historical_location_ids.length > 0 && (
                          <fieldset className="mt-3 space-y-2">
                            <legend className="text-sm font-medium">
                              {t("team.retired_history_access")}
                            </legend>
                            <p className="text-muted-foreground text-sm">
                              {t("team.retired_history_help")}
                            </p>
                            <input
                              type="hidden"
                              name="user[historical_location_ids][]"
                              value=""
                            />
                            {retiredLocations
                              .filter((location) =>
                                member.historical_location_ids.includes(
                                  location.id,
                                ),
                              )
                              .map((location) => (
                                <label
                                  key={location.id}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <Checkbox
                                    name="user[historical_location_ids][]"
                                    value={location.id}
                                    defaultChecked
                                    disabled={
                                      !canManageRoles || member.role === "admin"
                                    }
                                  />
                                  {location.name}
                                </label>
                              ))}
                          </fieldset>
                        )}
                        <FieldError>{errorText(errors.base)}</FieldError>
                      </>
                    )}
                  </Form>
                  <Form
                    action={userMemberActivationPath(member.id)}
                    method={member.deactivated_at ? "post" : "delete"}
                  >
                    {({ errors }) => (
                      <>
                        <Button
                          type="submit"
                          size="sm"
                          variant={
                            member.deactivated_at ? "outline" : "destructive"
                          }
                          disabled={
                            member.deactivated_at
                              ? !canInvite
                              : !member.can_deactivate
                          }
                        >
                          {member.deactivated_at
                            ? t("team.reactivate")
                            : t("team.deactivate")}
                        </Button>
                        <FieldError>{errorText(errors.base)}</FieldError>
                      </>
                    )}
                  </Form>
                </FocusPanel>
              </CardContent>
            </Card>
          ))}
        </div>
        {invitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("team.pending_invitations")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex justify-between border-b py-2 last:border-0"
                >
                  <span>{invitation.email_address}</span>
                  <Badge variant="outline">
                    {t(`team.${invitation.role}`)}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </SettingsLayout>
  );
}
